import { Billable, BillItem } from "./types";
import { TYPE_BILL_ITEM } from "../../../constants/types";
import { getBillItemTypeKey } from "../../../utils/billItemTypeOptions";

/**
 * SHER-460 — pure helpers backing the faster manual-billing autocomplete.
 *
 * Kept framework-free so the category-filter and free-item-type gating logic can
 * be unit-tested in isolation, away from the heavy redux/MUI wiring in BillItems.
 */

/**
 * SHER-460 #10 — synthetic category value for pharmacy items. Pharmacy billables
 * (TYPE_BILL_ITEM.PHARMACY_ITEM) usually carry no `category` string, so they never got a
 * filter chip even though they are searchable and add as "Item de Pharmacie" rows. This
 * sentinel gives them a dedicated chip; `filterBillablesByCategory` matches it by TYPE, and
 * the UI renders it with the translated "Item de Pharmacie" label (never shown raw).
 */
export const PHARMACY_CATEGORY = "__pharmacy_item__";

/**
 * Distinct, non-empty categories present among the given billables, sorted
 * alphabetically. Billables without a category (free/derived) are skipped, so the pill
 * row only shows categories that can actually filter. The pharmacy sentinel is appended
 * (last) whenever any pharmacy item is present, regardless of its category string.
 */
export function getBillableCategories(billables: Billable[]): string[] {
    const categories = new Set<string>();
    let hasPharmacy = false;
    for (const billable of billables) {
        const category = typeof billable.category === "string" ? billable.category.trim() : "";
        if (category) {
            categories.add(category);
        }
        if (Number(billable.type) === TYPE_BILL_ITEM.PHARMACY_ITEM) {
            hasPharmacy = true;
        }
    }
    const sorted = Array.from(categories).sort((a, b) => a.localeCompare(b));
    return hasPharmacy ? [...sorted, PHARMACY_CATEGORY] : sorted;
}

/**
 * SHER-460 — an "element" billable is a plain prestation line: everything except
 * forfaits (PACKAGE) and PERCENT_CHARGE templates, which are added through their own
 * dedicated paths. Shared by every manual-add UI so the exclusion rules can't drift.
 */
export function isElementBillable(billable: Billable): boolean {
    if (Number(billable.type) === TYPE_BILL_ITEM.PERCENT_CHARGE) return false;
    if (billable.type === TYPE_BILL_ITEM.PACKAGE) return false;
    return true;
}

export function getElementBillables(billables: Billable[]): Billable[] {
    return billables.filter(isElementBillable);
}

/**
 * SHER-460 #3 — order billables alphabetically by prestation/concept (locale-aware, accent- and
 * case-insensitive). Returns a new array; the catalogue uses this so the full list and any
 * filtered category/search read in a predictable order.
 */
export function sortBillablesByConcept(billables: Billable[]): Billable[] {
    return [...billables].sort((a, b) =>
        (a.concept ?? "").localeCompare(b.concept ?? "", undefined, { sensitivity: "base" }),
    );
}

/**
 * Narrow billables to a single category. A null/empty active category means "no
 * filter" and returns every billable unchanged.
 */
export function filterBillablesByCategory(billables: Billable[], activeCategory: string | null): Billable[] {
    if (!activeCategory) {
        return billables;
    }
    // SHER-460 #10: the pharmacy chip filters by TYPE, since pharmacy items have no category string.
    if (activeCategory === PHARMACY_CATEGORY) {
        return billables.filter((billable) => Number(billable.type) === TYPE_BILL_ITEM.PHARMACY_ITEM);
    }
    return billables.filter((billable) => {
        const category = typeof billable.category === "string" ? billable.category.trim() : "";
        return category === activeCategory;
    });
}

/**
 * A free item is in progress when the add-row has a concept typed in but no
 * catalogue billable was picked (no billableId). That is the state where the
 * cashier is creating a one-off line whose type can't be inherited from a
 * billable and must therefore be chosen by hand.
 */
export function isFreeItemInProgress(currentItem: Partial<BillItem> | undefined | null): boolean {
    if (!currentItem) {
        return false;
    }
    const concept = typeof currentItem.concept === "string" ? currentItem.concept.trim() : "";
    const hasBillable = currentItem.billableId !== undefined && currentItem.billableId !== null;
    return concept.length > 0 && !hasBillable;
}

/**
 * Curated manual Type options offered for a free item: CHARGE + SERVICE only
 * (SHER-460 decision). The internal/derived types (discounts, hidden value,
 * package, pharmacy, auto-anaesthesia, percent-charge) are deliberately excluded
 * — a cashier must never hand-pick them for a one-off line.
 */
export const FREE_ITEM_TYPE_OPTIONS: ReadonlyArray<{ key: string; value: number }> = [
    { key: "charge", value: TYPE_BILL_ITEM.CHARGE },
    { key: "service", value: TYPE_BILL_ITEM.SERVICE },
];

/**
 * Whether the value held in a free item's Type field is one of the curated,
 * selectable options. Used to make Type required so a free item can't be saved
 * with no (or an illegal) type.
 */
export function isValidFreeItemType(type: unknown): boolean {
    if (type === undefined || type === null || type === "") {
        return false;
    }
    return FREE_ITEM_TYPE_OPTIONS.some((option) => option.value === Number(type));
}

/**
 * SHER-546: whether a value is one of the real TYPE_BILL_ITEM members.
 *
 * Deliberately broader than isValidFreeItemType. Where the full Type column is on screen
 * it offers every type, so a cashier picking DISCOUNT_AMOUNT for a one-off line is
 * legitimate and must not be rejected. The only thing to reject there is "no type at
 * all" — which is what reached the table as `""` and crashed the positional lookup.
 *
 * Delegates to getBillItemTypeKey rather than re-deriving the answer from the enum's
 * reverse mapping: that lookup is the invariant being protected, so asking it directly is
 * what keeps "the validator accepts it" and "the table can render it" from ever
 * disagreeing. Two independent predicates for one rule is how this bug class regenerates.
 */
export function isBillItemType(type: unknown): boolean {
    return getBillItemTypeKey(type) !== null;
}
